import React from 'react';
import { FieldArray, useFormikContext } from 'formik';

import CheckboxFormik from '~/components/CheckboxFormik';
import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Text from '~/components/core/TextComponents/Text';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { TrashIcon } from '~/components/icons';
import InlineIconButtonUntyped from '~/components/InlineIconButton';
import TextFieldFormik from '~/components/TextFieldFormik';

import TEXTS from './texts';
import type { ConfigFormValues } from './types';
import { emptyPayloadField, PAYLOAD_FIELD_TYPE_LABELS, PAYLOAD_FIELD_TYPES } from './types';

// InlineIconButton is a .jsx file; under allowJs its optional props (including `ariaLabel`) are
// inferred as the literal `undefined` type. Cast to the runtime shape so a real label can be passed.
interface InlineIconButtonProps {
  icon: React.ElementType;
  tooltipTitle?: string;
  ariaLabel?: string;
  onClick?: () => void;
  disabled?: boolean;
}
const InlineIconButton = InlineIconButtonUntyped as React.ComponentType<InlineIconButtonProps>;

const PayloadFieldsEditor: React.FC = () => {
  const { values } = useFormikContext<ConfigFormValues>();

  return (
    <div className="flex flex-col gap-8">
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
        {TEXTS.fields.payloadFields}
      </Text>
      <FieldArray
        name="payload_fields"
        render={({ push, remove }) => (
          <div className="flex flex-col gap-16">
            {values.payload_fields.map((field, idx) => (
              <div className="flex flex-col gap-8 rounded border border-solid border-slate-300 p-12" key={idx}>
                <div className="grid grid-cols-2 gap-8">
                  <TextFieldFormik
                    id={`payload_fields.${idx}.key`}
                    name={`payload_fields.${idx}.key`}
                    label={TEXTS.payload.key}
                    fullWidth
                  />
                  <TextFieldFormik
                    select
                    id={`payload_fields.${idx}.field_type`}
                    name={`payload_fields.${idx}.field_type`}
                    label={TEXTS.payload.type}
                  >
                    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                    {/* @ts-ignore TextFieldFormik (JS) is not typed for select children */}
                    {PAYLOAD_FIELD_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {PAYLOAD_FIELD_TYPE_LABELS[type]}
                      </MenuItem>
                    ))}
                  </TextFieldFormik>
                </div>
                <TextFieldFormik
                  id={`payload_fields.${idx}.title`}
                  name={`payload_fields.${idx}.title`}
                  label={TEXTS.payload.title}
                  fullWidth
                />
                <TextFieldFormik
                  id={`payload_fields.${idx}.description`}
                  name={`payload_fields.${idx}.description`}
                  label={TEXTS.payload.description}
                  helperText={TEXTS.payload.descriptionHelper}
                  fullWidth
                  multiline
                />
                <div className="flex items-center justify-between">
                  <CheckboxFormik id={`payload_fields.${idx}.is_required`} label={TEXTS.payload.required} />
                  <InlineIconButton
                    icon={TrashIcon}
                    tooltipTitle="Remove field"
                    ariaLabel="Remove field"
                    onClick={() => remove(idx)}
                  />
                </div>
                {field.field_type === 'enum' && (
                  <FieldArray
                    name={`payload_fields.${idx}.enum_values`}
                    render={({ push: pushValue, remove: removeValue }) => (
                      <div className="flex flex-col gap-8">
                        <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.SEMI_BOLD}>
                          {TEXTS.payload.enumValues}
                        </Text>
                        {field.enum_values.map((_, valueIdx) => (
                          <div className="flex items-center gap-8" key={valueIdx}>
                            <TextFieldFormik
                              id={`payload_fields.${idx}.enum_values.${valueIdx}`}
                              name={`payload_fields.${idx}.enum_values.${valueIdx}`}
                              label={`${TEXTS.payload.enumValues} ${valueIdx + 1}`}
                              fullWidth
                            />
                            <InlineIconButton
                              icon={TrashIcon}
                              tooltipTitle="Remove value"
                              ariaLabel="Remove value"
                              onClick={() => removeValue(valueIdx)}
                            />
                          </div>
                        ))}
                        <Button
                          className="self-start"
                          variant="text"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={() => pushValue('')}
                        >
                          {TEXTS.payload.addEnumValue}
                        </Button>
                      </div>
                    )}
                  />
                )}
              </div>
            ))}
            <Button
              className="self-start"
              variant="text"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => push(emptyPayloadField())}
            >
              {TEXTS.payload.addField}
            </Button>
          </div>
        )}
      />
    </div>
  );
};

export default PayloadFieldsEditor;
